import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

const css = {
  card: { background:'var(--card-bg)', border:'1px solid rgba(201,168,76,0.18)', borderRadius:14, padding:'20px 22px', marginBottom:12 },
  input: { width:'100%', padding:'11px 14px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(201,168,76,0.22)', borderRadius:9, color:'#fff', fontFamily:"'Raleway',sans-serif", fontSize:'0.86rem', outline:'none' },
  label: { display:'block', fontSize:'0.66rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:5 },
  btnGold: { background:'linear-gradient(135deg,var(--primary),var(--primary-dark))', border:'none', color:'var(--bg-deep)', padding:'10px 24px', borderRadius:9, fontSize:'0.78rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.1em', fontFamily:"'Raleway',sans-serif" },
  btnGray: { background:'rgba(80,80,80,0.3)', border:'1px solid rgba(255,255,255,0.1)', color:'#888', padding:'9px 16px', borderRadius:8, fontSize:'0.72rem', cursor:'pointer' },
  btnRed: { background:'rgba(178,34,34,0.25)', border:'1px solid rgba(178,34,34,0.5)', color:'#ff8a8a', padding:'9px 18px', borderRadius:8, fontSize:'0.74rem', fontWeight:700, cursor:'pointer' },
  sectionTitle: { fontFamily:"'Cinzel Decorative',serif", fontSize:'1rem', color:'var(--primary)', marginBottom:20 },
};

export default function PastorsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name:'', title:'', bio:'', display_order:0 });
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef();

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('pastors').select('*').order('display_order', { ascending: true });
    if (data) setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name || !form.title) { alert('Name and title required'); return; }
    setSaving(true);

    let photo_url = editItem?.photo_url || null;

    // Upload new photo if selected
    if (photoFile) {
      setUploading(true);
      const fileName = `pastors/${Date.now()}_${photoFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(fileName, photoFile, { contentType: photoFile.type, upsert: true });
      if (!uploadError) {
        photo_url = supabase.storage.from('gallery').getPublicUrl(fileName).data.publicUrl;
      }
      setUploading(false);
    }

    const payload = { ...form, photo_url };

    if (editItem) {
      await supabase.from('pastors').update(payload).eq('id', editItem.id);
    } else {
      await supabase.from('pastors').insert([payload]);
    }

    setSaving(false);
    setShowForm(false);
    setEditItem(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setForm({ name:'', title:'', bio:'', display_order:0 });
    fetch();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this pastor?')) return;
    await supabase.from('pastors').delete().eq('id', id);
    fetch();
  };

  const startEdit = (p) => {
    setEditItem(p);
    setForm({ name: p.name, title: p.title, bio: p.bio || '', display_order: p.display_order || 0 });
    setPhotoPreview(p.photo_url || null);
    setPhotoFile(null);
    setShowForm(true);
  };

  return (
    <div>
      <div style={{ display:'flex', gap:10, marginBottom:24 }}>
        <button style={css.btnGold} onClick={() => { setShowForm(true); setEditItem(null); setPhotoFile(null); setPhotoPreview(null); setForm({ name:'', title:'', bio:'', display_order:0 }); }}>
          ➕ Add Pastor
        </button>
        <button onClick={fetch} style={css.btnGray}>🔄 Refresh</button>
      </div>

      {showForm && (
        <div style={{ background:'rgba(13,27,62,0.95)', border:'1px solid rgba(201,168,76,0.3)', borderRadius:14, padding:'28px 24px', marginBottom:24 }}>
          <h3 style={css.sectionTitle}>{editItem ? '✏️ Edit Pastor' : '➕ Add Pastor'}</h3>

          {/* Photo Upload */}
          <div style={{ marginBottom:22, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
            {/* Photo preview */}
            <div style={{
              width:100, height:80, borderRadius:'50%',
              border:'3px solid var(--primary)', overflow:'hidden',
              background:'rgba(201,168,76,0.08)',
              display:'flex', alignItems:'center', justifyContent:'center',
              flexShrink:0,
            }}>
              {photoPreview
                ? <img src={photoPreview} alt="preview" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <span style={{ fontSize:'2rem', color:'var(--primary)', opacity:0.4 }}>✝</span>
              }
            </div>
            <div>
              <label style={css.label}>Pastor Photo</label>
              <label htmlFor="pastorPhotoInput" style={{
                ...css.btnGold, display:'inline-block', cursor:'pointer',
                background:'linear-gradient(135deg,rgba(201,168,76,0.2),rgba(13,27,62,0.8))',
                border:'1px dashed rgba(201,168,76,0.5)', color:'var(--primary)',
                padding:'10px 20px', borderRadius:9,
              }}>
                📷 {photoPreview ? 'Change Photo' : 'Upload Photo'}
              </label>
              <input
                id="pastorPhotoInput"
                type="file"
                accept="image/*"
                style={{ display:'none' }}
                onChange={handlePhotoSelect}
                ref={photoRef}
              />
              {photoFile && (
                <div style={{ fontSize:'0.72rem', color:'#5cb85c', marginTop:6 }}>
                  ✅ {photoFile.name} ready to upload
                </div>
              )}
              {photoPreview && !photoFile && (
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:6 }}>
                  Current photo loaded
                </div>
              )}
            </div>
          </div>

          {/* Name & Title */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div>
              <label style={css.label}>Full Name *</label>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={css.input} placeholder="e.g. Pastor Philip Rajkumar"/>
            </div>
            <div>
              <label style={css.label}>Title *</label>
              <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={css.input} placeholder="e.g. Senior Pastor"/>
            </div>
          </div>

          {/* Bio */}
          <div style={{ marginBottom:14 }}>
            <label style={css.label}>Bio</label>
            <textarea value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))} style={{...css.input, height:100, resize:'vertical'}} placeholder="Pastor biography…"/>
          </div>

          {/* Display Order */}
          <div style={{ marginBottom:18 }}>
            <label style={css.label}>Display Order (1 = first)</label>
            <input type="number" value={form.display_order} onChange={e=>setForm(f=>({...f,display_order:parseInt(e.target.value)||0}))} style={{...css.input, width:120}} placeholder="0"/>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button style={css.btnGold} onClick={save} disabled={saving||uploading}>
              {uploading ? '⏳ Uploading photo…' : saving ? '⏳ Saving…' : '💾 Save Pastor'}
            </button>
            <button style={css.btnGray} onClick={() => { setShowForm(false); setEditItem(null); setPhotoFile(null); setPhotoPreview(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign:'center', color:'var(--primary)', padding:'40px' }}>Loading… 🙏</div>}

      {items.map(p => (
        <div key={p.id} style={css.card}>
          <div style={{ display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>
            {/* Photo */}
            <div style={{
              width:64, height:50, borderRadius:'50%',
              border:'2px solid var(--primary)', overflow:'hidden',
              background:'rgba(201,168,76,0.08)', flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              {p.photo_url
                ? <img src={p.photo_url} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <span style={{ fontSize:'1.4rem', color:'var(--primary)', opacity:0.4 }}>✝</span>
              }
            </div>
            {/* Info */}
            <div style={{ flex:1, minWidth:180 }}>
              <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.88rem', color:'var(--primary)', marginBottom:4 }}>{p.name}</div>
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>{p.title}</div>
              {p.bio && <p style={{ fontFamily:"'Crimson Pro',serif", fontSize:'0.88rem', color:'rgba(200,191,168,0.7)', lineHeight:1.55 }}>{p.bio.substring(0,120)}{p.bio.length>120?'…':''}</p>}
            </div>
            {/* Actions */}
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <button style={{...css.btnGray, color:'var(--primary)', border:'1px solid rgba(201,168,76,0.3)'}} onClick={() => startEdit(p)}>✏️ Edit</button>
              <button style={css.btnGray} onClick={() => del(p.id)}>🗑</button>
            </div>
          </div>
        </div>
      ))}

      {items.length === 0 && !loading && (
        <div style={{ textAlign:'center', color:'var(--text-muted)', padding:'40px', fontFamily:"'Crimson Pro',serif", fontSize:'1.05rem' }}>
          No pastors added yet. Click "Add Pastor" to get started! ✝
        </div>
      )}
    </div>
  );
}
